'use client';

import { useCallback, useState } from 'react';

import Link from 'next/link';

import { ArrowRight, CheckCircle2 } from 'lucide-react';

import { Alert, AlertDescription } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import { Checkbox } from '@kit/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';

import type {
  SaveActionResult,
  WorkspaceSaveTarget,
} from './tool-template.schema';

type Phase = 'select' | 'saving' | 'done' | 'error';

interface DialogState {
  phase: Phase;
  selected: Set<string>;
  result: SaveActionResult | null;
}

export interface WorkspaceSaveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The user's team workspaces, loaded by the CALLER before the dialog opens
   * (M1, Sanierungs-Spec §3): the island loads them in the save click, the
   * claim page passes its server-loaded list. No load phase in here – and the
   * former `loadTargets` reset effect (re-triggered by inline-arrow identity
   * on every parent render) is gone with it.
   */
  targets: WorkspaceSaveTarget[];
  /** Persists the config into the chosen workspaces (save or claim variant). */
  onConfirm: (accountIds: string[]) => Promise<SaveActionResult>;
}

/**
 * Multi-select workspace picker for CTA B (Spec §5.1). Each workspace gets an
 * independent template instance; it only becomes the default when the workspace
 * has none yet (UX-Bundle §6 – a save never replaces an existing default). The
 * per-workspace muted hint says whether it adds as an additional template or
 * becomes the default. Members without `settings.manage` are shown greyed-out
 * with a hint. Shared by the
 * logged-in marketing island and the claim page – which is why it lives under
 * `lib/tools/selbstauskunft` instead of a route-group `_components`
 * (Sanierungs-Spec §2.6/N6). Callers mount it conditionally, so the initial
 * state can derive from the `targets` prop.
 */
export function WorkspaceSaveDialog(props: WorkspaceSaveDialogProps) {
  const { open, targets } = props;

  const [state, setState] = useState<DialogState>(() => {
    const firstManageable = targets.find((t) => t.canManage);

    return {
      phase: 'select',
      selected: new Set(firstManageable ? [firstManageable.accountId] : []),
      result: null,
    };
  });

  const toggle = useCallback((accountId: string) => {
    setState((prev) => {
      const next = new Set(prev.selected);

      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }

      return { ...prev, selected: next };
    });
  }, []);

  const confirm = useCallback(async () => {
    const accountIds = Array.from(state.selected);

    if (accountIds.length === 0) return;

    setState((prev) => ({ ...prev, phase: 'saving' }));

    const result = await props.onConfirm(accountIds);

    setState((prev) => ({
      ...prev,
      phase: result.success ? 'done' : 'error',
      result,
    }));
  }, [props, state.selected]);

  const savedOutcomes = state.result?.outcomes.filter((o) => o.ok) ?? [];

  return (
    <Dialog open={open} onOpenChange={props.onOpenChange}>
      <DialogContent data-test="workspace-save-dialog">
        <DialogHeader>
          <DialogTitle>
            <Trans i18nKey="self-disclosure:save.title" />
          </DialogTitle>
          <DialogDescription>
            <Trans i18nKey="self-disclosure:save.description" />
          </DialogDescription>
        </DialogHeader>

        {state.phase === 'select' || state.phase === 'saving' ? (
          <div className="space-y-3">
            {targets.length === 0 ? (
              // Defensive only: both callers route 0-workspace users into the
              // onboarding park path BEFORE opening the picker (M1, Spec §3).
              <p className="text-muted-foreground text-sm">
                <Trans i18nKey="self-disclosure:save.noWorkspaces" />
              </p>
            ) : (
              <ul className="space-y-2">
                {targets.map((target) => (
                  <li
                    key={target.accountId}
                    className="rounded-lg border p-3"
                    data-test="workspace-save-option"
                  >
                    <label className="flex items-start gap-3">
                      <Checkbox
                        checked={state.selected.has(target.accountId)}
                        disabled={!target.canManage || state.phase === 'saving'}
                        onCheckedChange={() => toggle(target.accountId)}
                        data-test="workspace-save-checkbox"
                      />
                      <span className="min-w-0 flex-1 space-y-0.5">
                        <span className="text-foreground block text-sm font-medium">
                          {target.name}
                        </span>
                        {!target.canManage ? (
                          <span className="text-muted-foreground block text-xs">
                            <Trans i18nKey="self-disclosure:save.noPermission" />
                          </span>
                        ) : target.existingDefaultName ? (
                          <span className="text-muted-foreground block text-xs">
                            <Trans i18nKey="self-disclosure:save.addsAsAdditional" />
                          </span>
                        ) : (
                          <span className="text-muted-foreground block text-xs">
                            <Trans i18nKey="self-disclosure:save.becomesDefault" />
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="quiet"
                disabled={state.phase === 'saving'}
                onClick={() => props.onOpenChange(false)}
              >
                <Trans i18nKey="common:cancel" />
              </Button>
              <Button
                variant="primary"
                type="button"
                disabled={state.selected.size === 0 || state.phase === 'saving'}
                onClick={() => void confirm()}
                data-test="workspace-save-confirm"
              >
                {state.phase === 'saving' ? (
                  <Spinner size="sm" className="mr-2" variant="white" />
                ) : null}
                <Trans i18nKey="self-disclosure:save.confirm" />
              </Button>
            </DialogFooter>
          </div>
        ) : null}

        {state.phase === 'done' ? (
          <div className="space-y-3" data-test="workspace-save-success">
            <div className="flex items-center gap-2 text-sm font-medium">
              <CheckCircle2 className="text-success h-5 w-5" />
              <Trans i18nKey="self-disclosure:save.successTitle" />
            </div>
            <ul className="space-y-2">
              {savedOutcomes.map((outcome) => (
                <li key={outcome.accountId}>
                  {outcome.settingsHref ? (
                    <Button
                      asChild
                      variant="quiet"
                      className="w-full justify-between"
                    >
                      <Link href={outcome.settingsHref}>
                        <span>{outcome.name}</span>
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  ) : (
                    <span className="text-sm">{outcome.name}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {state.phase === 'error' ? (
          <Alert variant="destructive">
            <AlertDescription>
              {state.result?.error ?? (
                <Trans i18nKey="self-disclosure:save.genericError" />
              )}
            </AlertDescription>
          </Alert>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
